import { useEffect, useRef, useState } from "react";
import * as LucideIcons from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";

const DEFAULTS = {
  ar: {
    tag: "أرقام تصنع الفارق",
    title: "أثرنا بالأرقام",
    subtitle: "خبرة موثوقة وشراكات دائمة عبر مختلف القطاعات في العراق.",
    items: [
      { num: "150+", label: "مشروع مُنجز" },
      { num: "80+", label: "عميل يثق بنا" },
      { num: "12", label: "سنة خبرة" },
      { num: "24/7", label: "دعم متواصل" },
    ],
  },
  en: {
    tag: "Numbers That Matter",
    title: "Our impact in numbers",
    subtitle: "Trusted expertise and lasting partnerships across sectors in Iraq.",
    items: [
      { num: "150+", label: "Projects delivered" },
      { num: "80+", label: "Clients trust us" },
      { num: "12", label: "Years of experience" },
      { num: "24/7", label: "Continuous support" },
    ],
  },
  ku: {
    tag: "ژمارە کە جیاوازی دروست دەکەن",
    title: "کاریگەریمان بە ژمارە",
    subtitle: "شارەزایی متمانەپێکراو و هاوبەشی بەردەوام لە کەرتە جیاوازەکانی عێراق.",
    items: [
      { num: "150+", label: "پرۆژەی ئەنجامدراو" },
      { num: "80+", label: "کڕیاری متمانەکار" },
      { num: "12", label: "ساڵ شارەزایی" },
      { num: "24/7", label: "پشتگیری بەردەوام" },
    ],
  },
};

const ICONS = ["Rocket", "Users", "Award", "Headphones"];

// Extract numeric part + suffix from strings like "150+", "24/7", "12"
const splitNumber = (str) => {
  const s = String(str || "").trim();
  const m = s.match(/^(\d+(?:\.\d+)?)(.*)$/);
  if (!m) return { num: null, suffix: s };
  return { num: parseFloat(m[1]), suffix: m[2] };
};

function Counter({ value, duration = 1800 }) {
  const [display, setDisplay] = useState(value);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const { num, suffix } = splitNumber(value);
    if (num === null) { setDisplay(value); return; }
    setDisplay(`0${suffix}`);
    const el = ref.current;
    if (!el) return;
    const start = () => {
      if (started.current) return;
      started.current = true;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        const current = num * eased;
        const rounded = num >= 20 ? Math.round(current) : (Math.round(current * 10) / 10);
        setDisplay(`${rounded}${suffix}`);
        if (p < 1) requestAnimationFrame(tick);
        else setDisplay(`${num}${suffix}`);
      };
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) start(); });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{display}</span>;
}

export default function ImpactStats() {
  const { lang } = useLang();
  const { settings } = useSite();
  const d = DEFAULTS[lang] || DEFAULTS.ar;

  const tag = settings[`impact_tag_${lang}`] || d.tag;
  const title = settings[`impact_title_${lang}`] || d.title;
  const subtitle = settings[`impact_subtitle_${lang}`] || d.subtitle;

  const items = [1, 2, 3, 4].map((i) => ({
    num: settings[`impact_stat${i}_num`] || d.items[i - 1].num,
    label: settings[`impact_stat${i}_label_${lang}`] || d.items[i - 1].label,
    icon: ICONS[i - 1],
  }));

  return (
    <section id="impact" className="relative section-pad overflow-hidden" data-testid="impact-stats-section">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 50%, rgba(0,242,96,0.10), transparent 60%)" }} />
      <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <Reveal><SectionTag>{tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5">{title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">{subtitle}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {items.map((it, idx) => {
            const IconCmp = LucideIcons[it.icon] || LucideIcons.Sparkles;
            return (
              <Reveal key={idx} delay={0.1 + idx * 0.08}>
                <div
                  className="group relative rounded-3xl p-6 sm:p-8 text-center card-surface hover:border-[color:var(--primary)]/60 transition-all duration-500 hover:-translate-y-1"
                  style={{ boxShadow: "0 8px 40px -12px rgba(0,242,96,0.15)" }}
                  data-testid={`impact-stat-${idx}`}
                >
                  <div
                    className="mx-auto h-14 w-14 rounded-2xl grid place-items-center mb-4 border border-[color:var(--primary)]/30 bg-[color:var(--primary)]/10 group-hover:scale-110 transition-transform duration-500"
                    style={{ boxShadow: "0 0 30px rgba(0,242,96,0.15)" }}
                  >
                    <IconCmp size={22} className="text-primary" />
                  </div>
                  <div
                    className="text-4xl sm:text-5xl font-black tracking-tight text-primary mb-2"
                    style={{ textShadow: "0 0 30px rgba(0,242,96,0.35)", fontVariantNumeric: "tabular-nums" }}
                    data-testid={`impact-stat-num-${idx}`}
                  >
                    <Counter value={it.num} />
                  </div>
                  <div className="text-sm sm:text-base text-slate-300 font-semibold">{it.label}</div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
